import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { Link, useLocation } from '@reach/router';
import {ContextMenu, Icon, ListSeparator, Li} from '@startlibs/components';
import { fetchWithTokenRefresh, useAuth } from '../providers/AuthContext';
import { navigate } from '@reach/router';
import { getColor, getFetcher, getFetcherWithResponse } from '@startlibs/utils';
import { getJwt, setJwt } from '../hooks/useJwt';
import { setRefreshToken } from '../hooks/useRefresh';
import {Slot, usePopupToggle} from "@startlibs/core";

const Header = styled.header`
  width: 100%;
  background-color: white;
  // min-height: 62px;
  padding: 0px 15px;
  z-index: 1000; /* Ensure the header stays on top */
  position: relative;
  box-shadow: rgba(0, 0, 0, 0.15) 0px 0px 2px 0px, rgba(0, 0, 0, 0.2) 0px 0px 5px 1px;
`;

const NavbarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  min-height: 62px;
`;

const LogoWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const Logo = styled.a`
  img {
    margin-top: 15px;
    overflow-clip-margin: content-box;
    overflow: clip;
  }
  .l-logo{
    margin-left: 15px;
  }
  .md-b-logo{
    margin-bottom: 10px;
  }
`;

const NavbarToggle = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;

  @media (max-width: 768px) {
    display: block;
  }
`;

const HorizontalMenuMobile = styled.div`
  ul {
    display: flex;
    list-style: none;
    margin: 0;
    padding: 0;

    li {
      a {
        color: ${getColor('main')};
        font-size: 14px;
        text-decoration: none;
        padding: 22px 15px 23px 15px;
        transition: all 0.3s ease-in-out;

        &:hover {
          background: rgba(0, 139, 210, 0.075);
        }
      }
    }

    @media (max-width: 768px) {
      flex-direction: column;
      position: relative;
      display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
      background-color: white;
      width: 100%;
      padding: 10px 0;
      margin: 0;
      border-top: 1px solid #969696;

      li {
        a {
          padding: 14px;
          display: block;
          width: 100%;
          border-bottom: 1px solid #e5e5e5;

          &:hover {
            background: rgba(0, 139, 210, 0.075);
          }
        }
      }
    }
    @media (min-width: 768px) {
      display: none;
    }
  }
`;

const HorizontalMenu = styled.div`
  margin-left: 50px;

  ul {
    display: flex;
    list-style: none;
    margin: 0;
    padding: 0;
    li {
      white-space: nowrap;
      a {
        color: ${getColor('main')};
        font-size: 14px;
        text-decoration: none;
        padding: 22px 15px 23px 15px;
        transition: all 0.3s ease-in-out;

        &:hover {
          background: rgba(0, 139, 210, 0.075);
        }
      }
    }

    @media (max-width: 768px) {
      display: none;
      flex-direction: column;
      position: relative;
      background-color: white;
      width: 100%;
      padding: 10px 0;
      margin: 0;
      border-top: 1px solid #969696;

      li {
        a {
          padding: 14px;
          display: block;
          width: 100%;
          border-bottom: 1px solid #e5e5e5;

          &:hover {
            background: rgba(0, 139, 210, 0.075);
          }
        }
      }
    }
  }
`;

const ActiveLink = styled(Link)`
  font-weight: bold;
  color: #000;
  text-decoration: none;
  padding: 22px 20px 23px 20px;

  @media (max-width: 768px) {
    padding: 15px;
    display: block;
  }
`;

const ActiveLinkA = styled.a`
  font-weight: bold;
  color: #000;
  text-decoration: none;
  padding: 22px 20px 23px 20px;
  position: relative;

  @media (min-width: 769px) {
    :after {
      content: '';
      position: absolute;
      bottom: 2px;
      left: 1rem;
      right: 1rem;
      background: ${getColor('main')};
      height: 4px;
      border-radius: 4px 4px 0 0;
    }
  }

  @media (max-width: 768px) {
    padding: 15px;
    display: block;
  }
`;

const TopNav = styled.div`
  position: relative;
  display: inline-block;

  .user-icon {
    cursor: pointer;
    font-size: 1.5rem;
    display: flex;
    align-items: center;
  }

  .dropdown-menu {
    display: ${({ isDropdownOpen }) => (isDropdownOpen ? 'block' : 'none')};
    position: absolute;
    top: 34px;
    right: 0;
    background-color: white;
    border: 1px solid #969696;
    list-style: none;
    box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
    color: #667fa0;
    min-width: 100%; /* Matches the UserBox width */
    max-width: 300px; /* Ensure it doesn’t grow too large */
    box-sizing: border-box;

    li {
      a {
        display: block;
        padding: 0.5rem 1rem;
        font-size: 14px;
        text-decoration: none;
        width: 100%; /* Fills the parent’s width */
        box-sizing: border-box;
        text-align: center;

        &:hover {
          background-color: rgba(43, 121, 189, 0.66);
          color: #fff;
          cursor: pointer;
        }
      }
    }
  }
`;

const UserBox = styled.div`
  padding: 0.75rem;
  color: ${getColor('gray150')};
  font-size: 14px;
  cursor: pointer;
  max-width: 300px;
  border-radius: 5px;
  :hover {
    background: ${getColor('gray240')};
    color: ${getColor('gray120')};
  }
    ${Icon} {
      font-size: 12px;
    }
`;

const HeaderComponent = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { authUser, menu: allMenus, instanceData } = useAuth();
  const dropdownMenu = allMenus.filter(item => item.name === 'changePassword')
  const menu = allMenus.filter(item => item.name !== 'changePassword')
  const location = useLocation();
  const contextMenu = usePopupToggle()
  const defaultLogoURL = 'https://bucket-us-east-2.purview.net/logos/Purview_Logo_Horizontal_Main.png'

  useEffect(() => {

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    // Attach event listener for click events
    document.addEventListener('click', handleClickOutside);
    
    return () => {
      // Cleanup event listener on component unmount
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const doLogout = () => {
    fetchWithTokenRefresh(getFetcherWithResponse, '/gw/change-password', {}, {}, { jwt: getJwt(), setJwt: (jwt) => setJwt(jwt, 'worklist'), setRefreshToken: (refreshToken) => setRefreshToken(refreshToken, 'refreshToken')  }, 'login', true)
      .then(([data, res]) => {
        if (data.redirectUrl) {
          window.location.href = data.redirectUrl
        }
      })
  }

  return (
    <Header>
      <Slot name="Navbar-Popup" />
      <NavbarHeader>
        <LogoWrapper>
          <NavbarToggle
            type="button"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <Icon icon="menu" />
          </NavbarToggle>
          {/* <Logo as={Link} to="/" > */}
          <Logo href="/">
            <img
              href="/"
              src={instanceData?.logo}
              width={instanceData?.width}
              className={`logo ${instanceData?.classLogo}`}
              alt="Purview Logo"
            />
          </Logo>
          <HorizontalMenu isOpen={menuOpen}>
            <ul>
              {menu.map(item => (
                <li key={item.name}>
                  {location.pathname === item.path ? (
                    <ActiveLinkA href={item.path}>{item.label}</ActiveLinkA>
                  ) : (
                    <a href={item.path}>{item.label}</a>
                  )}
                </li>
              ))}
            </ul>
          </HorizontalMenu>
        </LogoWrapper>
        <TopNav isDropdownOpen={dropdownOpen} ref={dropdownRef}>
          <UserBox
            onClick={contextMenu.open}
          >
            <span style={{ marginRight: '4px' }}>
              {authUser ? authUser : 'Guest'}
            </span>
            <Icon icon="arrow-down" />
          </UserBox>
          {
            contextMenu.isOpen &&
            <ContextMenu slot="Navbar-Popup" withoutIcons>
              {
                dropdownMenu.map(item =>
                  item.name === "changePassword"
                    ? <Li key={item.name} label={item.label} onClick={() => doLogout()}/>
                    : <Li key={item.name} label={item.label} href={item.path}/>
              )}
              {!!dropdownMenu.length && <ListSeparator />}
              <Li label="Logout"
                onClick={() => {
                  getFetcherWithResponse('/gw/logout',{},{'headers': {'Authorization': 'Bearer ' + getJwt()}})
                    .then(([data, res]) => {
                      // console.log(data.ssoUrl)
                      localStorage.removeItem('worklist-jwt')
                      localStorage.removeItem('refreshToken')
                      if (data && data.customerInfo) {
                        const base64 = data.customerInfo
                        if (base64) {
                          let base64decoded = atob(base64)
                          let json = JSON.parse(base64decoded)
                          const ssoUrl = json?.ssoUrl ? json?.ssoUrl : false
                          if (ssoUrl) {
                            window.location.href = ssoUrl
                          }else{
                            navigate('/login', { replace: true });
                          }
                        }else{
                          navigate('/login', { replace: true });
                        }
                      }else{
                        navigate('/login', { replace: true });
                      }
                    })
                    .catch(err => {
                      console.log(err)
                      localStorage.removeItem('worklist-jwt')
                      localStorage.removeItem('refreshToken')
                      // navigate('/login')
                      // navigate to /login without back button
                      navigate('/login', { replace: true });
                    })
                }}/>
            </ContextMenu>
          }
        </TopNav>
      </NavbarHeader>
      <HorizontalMenuMobile isOpen={menuOpen}>
        <ul>
          {/* {menu.map(item => (
            <li key={item.name}>
              {location.pathname === item.path ? (
                <ActiveLink to={item.path}>{item.label}</ActiveLink>
              ) : (
                <Link to={item.path}>{item.label}</Link>
              )}
            </li>
          ))} */}
          {menu.map(item => (
            <li key={item.name}>
              {location.pathname === item.path ? (
                <ActiveLinkA href={item.path}>{item.label}</ActiveLinkA>
              ) : (
                <a href={item.path}>{item.label}</a>
              )}
            </li>
          ))} 
        </ul>
      </HorizontalMenuMobile>
    </Header>
  );
};

export default HeaderComponent;